import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import { useJoinMotion } from "./JoinMotion";
import CivixButton from "../../components/ui/CivixButton";
import { UserContext } from "../../context/UserContext";
import { getBillingErrorMessage, startBillingCheckout } from "../../utils/billingApi";
import { submitFoundingCohortApplication } from "../../utils/foundingCohortApi";
import {
  loadRecaptcha,
  renderRecaptcha,
  getRecaptchaToken,
  resetRecaptcha,
  isRecaptchaConfigured,
} from "../../utils/recaptcha";

const PLAN_BENEFITS = [
  {
    icon: CampaignOutlinedIcon,
    text: "Unlimited event and campaign creation",
  },
  {
    icon: SupportAgentOutlinedIcon,
    text: "Direct support from the CivixThread team",
  },
  {
    icon: AutoAwesomeOutlinedIcon,
    text: "New organizational tools as they launch",
  },
  {
    icon: RateReviewOutlinedIcon,
    text: "Opportunities to shape which features we improve, prioritize, and build next",
  },
];

const FREE_TIER_LIMITS = "1 active campaign and 1 published event per month";

const GOLD_PILL_SX = {
  display: "inline-flex",
  alignItems: "center",
  px: 1.75,
  py: 0.5,
  borderRadius: 999,
  border: "2px solid rgba(184, 134, 11, 0.45)",
  bgcolor: "rgba(251, 191, 36, 0.06)",
  boxShadow: "inset 0 0 0 1px rgba(255, 215, 0, 0.1)",
};

const PILL_CTA_SX = {
  width: { xs: "100%", sm: "auto" },
  minWidth: { sm: 140 },
  borderRadius: "999px",
  minHeight: 44,
  px: 2.5,
  fontSize: 13,
};

function GreenDividerLine() {
  return (
    <Box
      className="free-tier-divider-line"
      sx={{
        flex: 1,
        height: 4,
        borderRadius: 999,
        bgcolor: "var(--primary-color)",
        boxShadow: "0 1px 0 rgba(4, 120, 87, 0.2)",
        overflow: "hidden",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-28%",
          width: "28%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.15) 20%, rgba(248, 250, 252, 0.95) 50%, rgba(226, 232, 240, 0.15) 80%, transparent 100%)",
          opacity: 0,
          pointerEvents: "none",
        },
      }}
    />
  );
}

function FreeTierDivider() {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      role="presentation"
      aria-hidden
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        my: 1,
        ...(!reduceMotion && {
          "&:hover .free-tier-divider-line::after": {
            opacity: 1,
            animation: "freeTierDividerShimmer 2.4s ease-in-out",
          },
          "@keyframes freeTierDividerShimmer": {
            "0%": { left: "-28%" },
            "100%": { left: "100%" },
          },
        }),
      }}
    >
      <GreenDividerLine />
      <Typography
        sx={{
          fontSize: { xs: 13, md: 14 },
          fontWeight: 800,
          letterSpacing: 0.8,
          color: "var(--primary-color)",
          px: 0.5,
          flexShrink: 0,
        }}
      >
        OR
      </Typography>
      <GreenDividerLine />
    </Box>
  );
}

const CONTACT_INITIAL = {
  contact_name: "",
  work_email: "",
  organization_name: "",
  message: "",
  consent: false,
  company: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EarlyMemberPlanSection() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { fadeUp, hero } = useJoinMotion();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [contactValues, setContactValues] = useState(CONTACT_INITIAL);
  const [contactErrors, setContactErrors] = useState({});
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [contactError, setContactError] = useState("");
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaContainerReady, setRecaptchaContainerReady] = useState(false);
  const recaptchaContainerRef = useRef(null);

  useEffect(() => {
    if (!recaptchaContainerReady || !isRecaptchaConfigured()) return undefined;

    let cancelled = false;
    const initRecaptcha = async () => {
      try {
        await loadRecaptcha();
        if (cancelled) return;
        const id = "recaptcha-container-early-member";
        const delay = window.requestAnimationFrame
          ? () =>
              new Promise((resolve) => {
                requestAnimationFrame(() => requestAnimationFrame(resolve));
              })
          : () => new Promise((resolve) => setTimeout(resolve, 50));
        await delay();
        if (cancelled || !document.getElementById(id)) return;
        const widgetId = await renderRecaptcha(id, { action: "founding_apply", size: "normal" });
        if (widgetId !== null) setRecaptchaReady(true);
      } catch (err) {
        if (!cancelled) console.error("Failed to initialize reCAPTCHA:", err);
      }
    };
    initRecaptcha();
    return () => {
      cancelled = true;
    };
  }, [recaptchaContainerReady]);

  const handleEnroll = async () => {
    if (!user) {
      navigate(`/auth/login?redirect=${encodeURIComponent("/features#organizer-plan")}`);
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const { checkout_url: checkoutUrl } = await startBillingCheckout("organizer_intro");
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      setCheckoutError(getBillingErrorMessage(err, { action: "start checkout" }));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (contactErrors[name]) {
      setContactErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateContact = () => {
    const errors = {};
    if (!contactValues.contact_name.trim()) errors.contact_name = "Required";
    if (!contactValues.work_email.trim()) errors.work_email = "Required";
    else if (!EMAIL_RE.test(contactValues.work_email.trim())) errors.work_email = "Enter a valid email";
    if (!contactValues.organization_name.trim()) errors.organization_name = "Required";
    if (!contactValues.consent) errors.consent = "Required to submit";
    return errors;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError("");
    const errors = validateContact();
    if (Object.keys(errors).length) {
      setContactErrors(errors);
      setContactError("Please fix the highlighted fields.");
      return;
    }
    if (contactValues.company) return;
    if (isRecaptchaConfigured() && !getRecaptchaToken()) {
      setContactError('Please complete the "I\'m not a robot" checkbox.');
      return;
    }

    setContactSubmitting(true);
    try {
      await submitFoundingCohortApplication({
        contact_name: contactValues.contact_name.trim(),
        work_email: contactValues.work_email.trim(),
        organization_name: contactValues.organization_name.trim(),
        organization_type: "organization",
        role_title: "Organizer Plan inquiry",
        website: "",
        intended_use:
          contactValues.message.trim() ||
          "Interested in speaking with the CivixThread team about the Organizer Plan.",
        expected_scale: "",
        consent: contactValues.consent,
        recaptcha_token: getRecaptchaToken() || undefined,
      });
      setContactDone(true);
      setContactValues(CONTACT_INITIAL);
      setContactErrors({});
      resetRecaptcha();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 409) {
        setContactError(msg || "We already have a message from this email. Our team will be in touch.");
      } else if (status === 429) {
        setContactError(msg || "Too many attempts. Please try again later.");
      } else {
        setContactError(msg || "Unable to send your message. Please try again.");
      }
      resetRecaptcha();
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <Box id="organizer-plan" sx={{ scrollMarginTop: { xs: 120, md: 128 }, pt: { xs: 2, md: 3 } }}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Box component={motion.div} {...hero}>
            <SectionHeader
              align="left"
              variant="standard"
              eyebrow="A voice in CivixThread's future"
              eyebrowSx={{ fontSize: { xs: 13, md: 14 }, letterSpacing: 1.1, mb: 1 }}
              title="Enroll in the Organizer Plan"
              description="Early members pay $99/month for the first year, then $149/month. You'll get expanded outreach tools, direct team support, and a voice in what we build next."
              descriptionSx={{ fontSize: { xs: 16, md: 18 } }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
              {PLAN_BENEFITS.map(({ icon: Icon, text }) => (
                <Box key={text} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                  <Icon sx={{ fontSize: 22, color: "var(--primary-color)", mt: 0.25 }} aria-hidden />
                  <Typography sx={{ fontSize: { xs: 16, md: 17 }, color: "var(--black-color)", lineHeight: 1.55 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
              <FreeTierDivider />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.25,
                  pt: 0.5,
                }}
              >
                <Box component="span" sx={GOLD_PILL_SX}>
                  <Typography
                    sx={{
                      fontSize: { xs: 13, md: 14 },
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: "#92650a",
                      lineHeight: 1.2,
                    }}
                  >
                    Free Tier
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: 16, md: 17 }, color: "var(--dna-color-palette-black-400)", lineHeight: 1.55 }}>
                  {FREE_TIER_LIMITS}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            component={motion.div}
            {...fadeUp}
            sx={{
              height: "100%",
              p: { xs: 2.5, md: 3 },
              borderRadius: "var(--radius-card)",
              border: "1px solid var(--join-border-muted)",
              bgcolor: "var(--join-surface-elevated)",
              boxShadow: "var(--join-shadow-elevated)",
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 18, md: 20 },
                color: "var(--black-color)",
                lineHeight: 1.5,
                mb: 0.75,
              }}
            >
              Create an account and enroll through secure checkout
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 15, md: 16 },
                color: "var(--dna-color-palette-black-400)",
                lineHeight: 1.5,
                mb: 1.5,
              }}
            >
              Or talk to our team first if you have questions.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 36, md: 44 }, color: "var(--primary-color)", lineHeight: 1 }}>
                $99
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "var(--black-color)" }}>
                / month
              </Typography>
              <Typography sx={{ fontSize: 14, color: "var(--dna-color-palette-black-400)" }}>
                for the first year, then $149/mo
              </Typography>
            </Box>

            {checkoutError && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {checkoutError}
              </Alert>
            )}

            <Box sx={{ mb: 2.5 }}>
              <CivixButton
                label={checkoutLoading ? "Starting checkout…" : "Get Started"}
                onClick={handleEnroll}
                disabled={checkoutLoading}
                loading={checkoutLoading}
                variant="pill"
                size="sm"
                sx={PILL_CTA_SX}
              />
              <Typography sx={{ fontSize: 13, color: "var(--dna-color-palette-black-400)", mt: 1.5, lineHeight: 1.6 }}>
                {user
                  ? "After payment is secured, a member of our team will contact you to help you get started on the Organizer Plan."
                  : "Once payment is secured, someone on our team will reach out to help you get started."}
              </Typography>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography component="h3" sx={{ fontWeight: 800, fontSize: 18, color: "var(--black-color)", mb: 0.5 }}>
              Speak with our team
            </Typography>
            <Typography sx={{ fontSize: 14, color: "var(--dna-color-palette-black-400)", lineHeight: 1.6, mb: 2 }}>
              Prefer a conversation first? Send a quick note and a member of our team will contact you.
            </Typography>

            {contactDone ? (
              <Alert severity="success" role="status">
                Thanks — we received your message. A member of our team will be in touch soon.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleContactSubmit} noValidate>
                {contactError && (
                  <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                    {contactError}
                  </Alert>
                )}

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Your name"
                      name="contact_name"
                      value={contactValues.contact_name}
                      onChange={handleContactChange}
                      error={Boolean(contactErrors.contact_name)}
                      helperText={contactErrors.contact_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Work email"
                      name="work_email"
                      value={contactValues.work_email}
                      onChange={handleContactChange}
                      error={Boolean(contactErrors.work_email)}
                      helperText={contactErrors.work_email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Organization name"
                      name="organization_name"
                      value={contactValues.organization_name}
                      onChange={handleContactChange}
                      error={Boolean(contactErrors.organization_name)}
                      helperText={contactErrors.organization_name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="What would you like to discuss? (optional)"
                      name="message"
                      value={contactValues.message}
                      onChange={handleContactChange}
                      placeholder="e.g. onboarding timeline, campaign setup, team training"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: "none" }} aria-hidden="true">
                  <TextField
                    fullWidth
                    label="Company"
                    name="company"
                    value={contactValues.company}
                    onChange={handleContactChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </Box>

                <FormControlLabel
                  sx={{
                    mt: 1.5,
                    alignItems: "center",
                    mx: 0,
                    "& .MuiCheckbox-root": { py: 0.25 },
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                      lineHeight: 1.4,
                      color: contactErrors.consent ? "#b91c1c" : "var(--black-color)",
                    },
                  }}
                  control={
                    <Checkbox
                      name="consent"
                      checked={contactValues.consent}
                      onChange={handleContactChange}
                      sx={{ color: "var(--primary-color)", p: 0.5 }}
                    />
                  }
                  label="I agree to be contacted about the Organizer Plan."
                />

                {isRecaptchaConfigured() && (
                  <Box
                    ref={(el) => {
                      recaptchaContainerRef.current = el;
                      if (el && !recaptchaContainerReady) setRecaptchaContainerReady(true);
                    }}
                    sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}
                  >
                    <div id="recaptcha-container-early-member" />
                  </Box>
                )}

                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <CivixButton
                    type="submit"
                    label={contactSubmitting ? "Submitting…" : "Submit"}
                    disabled={contactSubmitting || (isRecaptchaConfigured() && !recaptchaReady)}
                    loading={contactSubmitting}
                    variant="pill"
                    size="sm"
                    sx={{
                      ...PILL_CTA_SX,
                      minWidth: { xs: "100%", sm: 180 },
                      px: 3.5,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
